import { parseTypeName } from './resource';
import { AnyResourceDefinition } from './types';
import { Link, LinkBehavior, TagResult } from '../links/definition/link';
import { create } from 'event-emitter';
import { indexedMap } from '../utils/indexedMap';

type TraversalSide = {
  state: any;
  params: any;
  context: unknown;
};

export type ResolvedHandler = (opts: {
  dataChanged: boolean;
  flagsChanged: boolean;
  parent: TraversalSide;
  child: TraversalSide;
  getContexts: () => { parent: unknown; child: unknown }[];
}) => any;

export type ResolvedEdge = {
  linkKey: string;
  behavior: LinkBehavior;
  resolve?: ResolvedHandler;
};

export type ResourceRegistryMetadata = {
  link: Link;
  registeringResource: string;
  tags?: (opts: { params: any; data?: any }) => TagResult[] | void;
  tagData?: (opts: { data: any; params: any }) => TagResult[] | void;
  outgoing: ResolvedEdge;
  incoming: ResolvedEdge;
};

export type SchemaChangeEvent = {
  added: Link[] | null;
  removed: Link[] | null;
  typeName: string;
};

export type ResourceRemovedEvent = {
  typeName: string;
};

const wrapHandler = (
  handler: Link['onPush'] | Link['onPull'],
  selfIsParent: boolean
): ResolvedHandler | undefined =>
  handler &&
  ((opts) =>
    handler({
      dataChanged: opts.dataChanged,
      flagsChanged: opts.flagsChanged,
      self: selfIsParent ? opts.parent : opts.child,
      other: selfIsParent ? opts.child : opts.parent,
      getContexts: () =>
        opts
          .getContexts()
          .map((c) =>
            selfIsParent
              ? { self: c.parent, other: c.child }
              : { self: c.child, other: c.parent }
          ),
    }));

export const resolveEdges = (
  link: Link
): { push: ResolvedEdge; pull: ResolvedEdge } => ({
  push: {
    linkKey: link.key,
    behavior: link.pushBehavior,
    resolve: wrapHandler(link.onPush, true),
  },
  pull: {
    linkKey: link.key,
    behavior: link.pullBehavior,
    resolve: wrapHandler(link.onPull, false),
  },
});

const SELF_LINK_TARGET = '__pulse:self__';

const concatTagFns = <T>(
  a: ((opts: T) => TagResult[] | void) | undefined,
  b: ((opts: T) => TagResult[] | void) | undefined
): ((opts: T) => TagResult[] | void) | undefined => {
  if (!a) {
    return b;
  }
  if (!b) {
    return a;
  }
  return (opts: T) => {
    const ra = a(opts);
    const rb = b(opts);
    if (!ra) {
      return rb;
    }
    if (!rb) {
      return ra;
    }
    return [...ra, ...rb];
  };
};

export const createResourceRegistry = () => {
  const nodes: Map<string, AnyResourceDefinition> = new Map();
  const edges = indexedMap<ResourceRegistryMetadata>();

  const emitter = create<{
    schemaChange: SchemaChangeEvent;
    resourceRemoved: ResourceRemovedEvent;
  }>();

  const visitLinks = (
    typeName: string,
    visitor: (metadata: ResourceRegistryMetadata) => void
  ) => {
    const outgoing = edges.getAll(typeName);
    if (outgoing) {
      for (const metadata of outgoing.values()) {
        visitor(metadata);
      }
    }
  };

  const addLink = (registeringResource: string, link: Link) => {
    const { selfTypeName, otherTypeName } = link;
    const { push, pull } = resolveEdges(link);

    if (selfTypeName === otherTypeName) {
      edges.set(selfTypeName, SELF_LINK_TARGET, {
        link,
        registeringResource,
        tags: concatTagFns(link.tagParams?.self, link.tagParams?.other),
        tagData: concatTagFns(link.tagData?.self, link.tagData?.other),
        outgoing: push,
        incoming: pull,
      });
    } else {
      edges.set(selfTypeName, otherTypeName, {
        link,
        registeringResource,
        tags: link.tagParams?.self,
        tagData: link.tagData?.self,
        outgoing: push,
        incoming: pull,
      });

      edges.set(otherTypeName, selfTypeName, {
        link,
        registeringResource,
        tags: link.tagParams?.other,
        tagData: link.tagData?.other,
        outgoing: pull,
        incoming: push,
      });
    }
  };

  const removeLink = (link: Link) => {
    const { selfTypeName, otherTypeName } = link;

    if (selfTypeName === otherTypeName) {
      edges.delete(selfTypeName, SELF_LINK_TARGET);
    } else {
      edges.delete(selfTypeName, otherTypeName);
      edges.delete(otherTypeName, selfTypeName);
    }
  };

  const registerResource = (
    resource: AnyResourceDefinition,
    links: Link[] = []
  ) => {
    const priorLinks: Link[] = [];

    visitLinks(resource.typeName, ({ registeringResource, link }) => {
      if (registeringResource === resource.typeName) {
        priorLinks.push(link);
      }
    });

    nodes.set(resource.typeName, resource);

    const newKeys = new Set(links.map((l) => l.key));
    const priorKeys = new Set(priorLinks.map((l) => l.key));

    let removed: Link[] | null = null;

    for (const prior of priorLinks) {
      if (!newKeys.has(prior.key)) {
        removeLink(prior);
        (removed ??= []).push(prior);
      }
    }

    let added: Link[] | null = null;

    for (const link of links) {
      addLink(resource.typeName, link);

      if (!priorKeys.has(link.key)) {
        (added ??= []).push(link);
      }
    }

    if (added || removed || priorLinks.length > 0) {
      emitter.emit('schemaChange', {
        added,
        removed,
        typeName: resource.typeName,
      });
    }
  };

  const removeResource = (typeName: string) => {
    if (!nodes.has(typeName)) {
      return;
    }

    const linksToRemove: Link[] = [];
    visitLinks(typeName, ({ link }) => {
      linksToRemove.push(link);
    });

    for (const link of linksToRemove) {
      removeLink(link);
    }

    emitter.emit('schemaChange', {
      added: null,
      removed: linksToRemove.length > 0 ? linksToRemove : null,
      typeName,
    });

    nodes.delete(typeName);

    emitter.emit('resourceRemoved', { typeName });
  };

  const getResourceFromTypeName = (typeName: string) => {
    const def = nodes.get(typeName);

    if (!def) {
      throw new Error(`Resource ${typeName} not found in registry`);
    }

    return def as AnyResourceDefinition;
  };

  const serialize = () => ({
    nodes: [...nodes].map(([id, data]) => ({
      id,
      data,
    })),
    edges: [...edges].map(([sourceId, targetId, metadata]) => ({
      sourceId,
      targetId,
      metadata,
    })),
  });

  return {
    emitter,
    serialize,
    getLinkInfo: (selfTypeName: string, otherTypeName: string) =>
      edges.get(selfTypeName, otherTypeName) ??
      (selfTypeName === otherTypeName
        ? edges.get(selfTypeName, SELF_LINK_TARGET)
        : undefined),
    forEachLink: visitLinks,
    getResourceFromTypeName,
    getResource: (instanceKey: string) =>
      getResourceFromTypeName(parseTypeName(instanceKey)),
    getAllResources: () =>
      Object.fromEntries(nodes) as Record<string, AnyResourceDefinition>,
    registerResource,
    removeResource,
  };
};

export let defaultResourceRegistry = createResourceRegistry();

export const __testOnly_resetDefaultRegistry = () => {
  defaultResourceRegistry = createResourceRegistry();
};

export type ResourceRegistry = ReturnType<typeof createResourceRegistry>;
